'use client'

import { parseDate, type DateValue } from '@internationalized/date'
import { Input } from '@nextui-org/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { CustomCalendar } from './customCalendar'

type Props = {
  lang?: string
  label: string
  value?: string
  minDate?: DateValue
  maxDate?: DateValue
  errorMessage?: any
  isDateUnavailable?: (date: DateValue) => boolean
  onChange?: (value: DateValue) => void
}

const dateValueToString = (date: DateValue | undefined): string | undefined => {
  if (!date) {
    return undefined
  }
  const month = String(date.month).padStart(2, '0')
  const day = String(date.day).padStart(2, '0')
  return `${date.year}-${month}-${day}`
}

export function DatePicker({
  label,
  lang,
  value,
  minDate,
  maxDate,
  errorMessage,
  isDateUnavailable,
  onChange
}: Props) {
  const [internalValue, setInternalValue] = useState('')
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!value) {
      setInternalValue('')
    }
  }, [value])

  const handleDateSelect = (dateValue: DateValue) => {
    const dateString = dateValueToString(dateValue)
    if (dateString) {
      setInternalValue(dateString)
      if (onChange) {
        onChange(dateValue)
      }
    }
  }
  const handleCalendarClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsCalendarOpen(true)
  }

  const handleCalendarTouch = (e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsCalendarOpen(true)
  }

  const handleReset = () => {
    setInternalValue('')
    if (onChange) {
      // Call onChange with undefined or handle reset logic
    }
    setIsCalendarOpen(false)
  }

  // Format display value
  const displayValue = useMemo(() => {
    if (!internalValue) return ''
    try {
      const date = parseDate(internalValue)
      // Use locale-aware formatting
      const jsDate = new Date(date.year, date.month - 1, date.day)
      return jsDate.toLocaleDateString(lang || 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return internalValue
    }
  }, [internalValue, lang])

  return (
    <>
      {' '}
      <div
        className='group z-10 flex w-full flex-col subpixel-antialiased aria-expanded:scale-[0.97] aria-expanded:opacity-70 data-[hidden=true]:hidden'
        onClick={handleCalendarClick}
        onTouchEnd={handleCalendarTouch}
        style={{ WebkitTapHighlightColor: 'transparent' }}>
        <Input
          ref={inputRef}
          type='text'
          readOnly
          classNames={{
            label: 'text-theme/90',
            input: `text-left text-sm font-bold !text-theme cursor-pointer ${!internalValue ? 'text-transparent' : ''}`,
            inputWrapper:
              'rounded !bg-theme-200/20 shadow-none hover:!bg-theme-200/25 cursor-pointer'
          }}
          size='sm'
          radius='none'
          labelPlacement='inside'
          label={label}
          value={displayValue}
          isRequired
          errorMessage={errorMessage}
          // placeholder='Select date'
          style={{
            colorScheme: 'light'
          }}
        />
      </div>{' '}
      <CustomCalendar
        locale={lang}
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        minDate={minDate}
        maxDate={maxDate}
        isDateUnavailable={isDateUnavailable}
        onDateSelect={handleDateSelect}
        currentValue={internalValue}
      />
    </>
  )
}
